import React, { useState } from 'react';
import classNames from 'classnames';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import TextInputWithLabel from '../../../components/fields/TextInput/TextInputWithLabel';
import SelectInput from '../../../components/fields/SelectInput/SelectInput';
import DatePicker from '../../../components/fields/DatePicker/DatePicker';
import CustomButton from '../../../components/base/Button/CustomButton';
import FileInput from '../../../components/fields/FileInput/FileInput';
import ClientCardInfo from './ClientCardInfo';
import { lpnTypeOptions, vehicleTypeOptions } from '../constants';
import { getZoneData } from '../../Zones/reducer';
import { getClientData } from '../reducer';
import FieldDTO from '../../../components/fields/Abstract/FieldDTO';
import { createUser, updateUser } from '../actions';
import notificationDecorator from '../../Notifications/notificationDecorator';
import { responseLogWithNotification } from '../../../utils/utils';
import Switches from '../../../components/fields/Switches/Switches';
import defaultCarImg from '../../../theme/img/default-car.png';
import ImagePreviewModal from '../../../components/base/ImagePreviewModal';

export default function ClientForm(props) {
  const {
    onHide = () => {},
    selectedItem = null,
    mode,
    loadData,
  } = props;
  const dispatch = useDispatch();
  const { zoneList } = useSelector(getZoneData);
  const { clientTypeList } = useSelector(getClientData);
  const [entryImageFile, setEntryImageFile] = useState(null);
  const [exitImageFile, setExitImageFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');

  const zoneOptions = zoneList.map(zone => ({ name: zone.name, value: zone.id }));
  const clientTypeOptions = clientTypeList.map(clientType => ({ name: clientType.label, value: clientType.value }));

  const entryImage = selectedItem?.entry_image || null;
  const exitImage = selectedItem?.exit_image || null;

  const initial = {};
  if (selectedItem) {
    initial.id = selectedItem.id;
    initial.lpn_type = selectedItem.lpn_type;
    initial.vehicle_type = selectedItem.vehicle_type;
    initial.type = selectedItem.type;
    initial.lpn = selectedItem.lpn;
    initial.parking_zone_id = selectedItem.parking_zone_id;
    initial.issue_at = selectedItem.issue_at || null;
    initial.expiry_at = selectedItem.expiry_at || null;
    initial.is_active = selectedItem.is_active;
  }

  const {
    control, getValues,
  } = useForm({
    defaultValues: initial,
  });

  const openPreview = (imageUrl, title) => {
    if (!imageUrl) {
      return;
    }

    setPreviewImage(imageUrl);
    setPreviewTitle(title);
  };

  const closePreview = () => {
    setPreviewImage(null);
    setPreviewTitle('');
  };

  const inferLpnTypeByValue = value => {
    const normalized = String(value || '').trim().toUpperCase().replace(/[\s-]+/g, '');
    if (!normalized) {
      return null;
    }

    if (/^\d{2}[A-Z]{2}\d{3}$/.test(normalized)) {
      return 'am';
    }

    if (/^[ABEKMHOPCTYXАВЕКМНОРСТУХ]\d{3}[ABEKMHOPCTYXАВЕКМНОРСТУХ]{2}\d{2,3}$/.test(normalized)) {
      return 'ru';
    }

    return selectedItem?.lpn_type || 'ru';
  };

  const toUpdatePayload = data => {
    const payload = {};

    if (data.vehicle_type) {
      payload.vehicle_type = data.vehicle_type;
    }

    const lpnValue = String(data.lpn || '').trim();
    if (lpnValue) {
      payload.lpn = lpnValue;
      payload.lpn_type = inferLpnTypeByValue(lpnValue);
    }

    return payload;
  };

  const updateClient = () => {
    const data = getValues();
    const userData = mode === 'edit' ? toUpdatePayload(data) : { ...data };

    if (mode !== 'edit' && entryImageFile) {
      userData.entry_image_file = entryImageFile;
    }

    if (mode !== 'edit' && exitImageFile) {
      userData.exit_image_file = exitImageFile;
    }

    const action = mode === 'edit'
      ? updateUser({ id: data.id, userData })
      : createUser({ userData });

    dispatch(action)
      .then(() => {
        notificationDecorator({
          title: 'Редактирование клиента',
          message: 'Редактирование клиента успешно',
        });
        onHide();
        loadData();
      })
      .catch(responseLogWithNotification);
  };

  return (
    <div className="client-form">
      <span className="client-form-title">Основная информация</span>
      { mode === 'edit' ? (
        <div className="client-form-photo-list">
          <div className="client-form-photo-item">
            <div className="client-form-photo-title">Фото въезда</div>
            <div
              className={classNames('client-form-photo-image', { clickable: Boolean(entryImage) })}
              style={{ backgroundImage: `url(${entryImage || defaultCarImg})` }}
              onClick={() => openPreview(entryImage, 'Фото въезда')}
            />
          </div>
          <div className="client-form-photo-item">
            <div className="client-form-photo-title">Фото выезда</div>
            <div
              className={classNames('client-form-photo-image', { clickable: Boolean(exitImage) })}
              style={{ backgroundImage: `url(${exitImage || defaultCarImg})` }}
              onClick={() => openPreview(exitImage, 'Фото выезда')}
            />
          </div>
        </div>
      ) : null}
      <div className="client-form-data">
        <div className="client-form-data-box">
          { mode === 'edit' ? (
            <FieldDTO
              control={control}
              type="text"
              className="short"
              disabled
              component={TextInputWithLabel}
              dtoObject={{
                name: 'id',
                type: String,
                validators: [],
                labelKey: 'client.client_id',
              }}
            />
          ) : null}
          { mode !== 'edit' ? (
            <FieldDTO
              control={control}
              type="text"
              className="short"
              dtoObject={{
                name: 'type',
                type: String,
                validators: [],
                labelKey: 'client.clientType',
              }}
              withLabel
              addPlaceholder
              component={SelectInput}
              options={clientTypeOptions}
            />
          ) : null}
          { mode !== 'edit' ? (
            <FieldDTO
              control={control}
              type="text"
              className="short"
              dtoObject={{
                name: 'parking_zone_id',
                type: String,
                validators: [],
                labelKey: 'client.zone',
              }}
              withLabel
              addPlaceholder
              component={SelectInput}
              options={zoneOptions}
            />
          ) : null}
          <FieldDTO
            control={control}
            type="text"
            className="short"
            component={TextInputWithLabel}
            dtoObject={{
              name: 'lpn',
              type: String,
              validators: [],
              labelKey: 'client.lpn',
            }}
          />
          { mode !== 'edit' ? (
            <FieldDTO
              control={control}
              type="text"
              className="short"
              dtoObject={{
                name: 'lpn_type',
                type: String,
                validators: [],
                labelKey: 'client.lpn_type',
              }}
              withLabel
              addPlaceholder
              component={SelectInput}
              options={lpnTypeOptions}
            />
          ) : null}
          { mode !== 'edit' ? (
            <FieldDTO
              control={control}
              component={DatePicker}
              hideSmallButtons
              showArrowIcon
              withLabel
              label="Срок действия с"
              dtoObject={{
                name: 'issue_at',
                type: String,
                validators: [],
                labelKey: 'client.dateStart',
              }}
              usePortal
              className="short"
              onChangeToIso
            />
          ) : null}
          { mode !== 'edit' ? (
            <FieldDTO
              control={control}
              component={DatePicker}
              hideSmallButtons
              showArrowIcon
              usePortal
              withLabel
              label="Срок действия по"
              onChangeToIso
              dtoObject={{
                name: 'expiry_at',
                type: String,
                validators: [],
                labelKey: 'client.dateEnd',
              }}
              className="short"
            />
          ) : null}
          <FieldDTO
            control={control}
            type="text"
            className="short"
            dtoObject={{
              name: 'vehicle_type',
              type: String,
              validators: [],
              labelKey: 'client.vehicle_type',
            }}
            withLabel
            addPlaceholder
            component={SelectInput}
            options={vehicleTypeOptions}
          />
          { mode !== 'edit' ? (
            <FieldDTO
              control={control}
              type="text"
              component={Switches}
              dtoObject={{
                name: 'is_active',
                type: Number,
                validators: [],
                labelKey: 'elp.is_active',
              }}
              className="filter-margin"
              withLabel
              addPlaceholder
            />
          ) : null}
          { mode !== 'edit' ? (
            <div className="form-control-wrapper short">
              <div className="form-label">Фото въезда</div>
              <FileInput
                variant="default"
                label="Выберите изображение"
                input={{
                  onChange: file => setEntryImageFile(file && file.get ? file.get('file') : null),
                }}
              />
            </div>
          ) : null}
          { mode !== 'edit' ? (
            <div className="form-control-wrapper short">
              <div className="form-label">Фото выезда</div>
              <FileInput
                variant="default"
                label="Выберите изображение"
                input={{
                  onChange: file => setExitImageFile(file && file.get ? file.get('file') : null),
                }}
              />
            </div>
          ) : null}
          <div className="empty" />
        </div>
        { selectedItem && mode === 'edit' ? (
          <div className="client-form-data-card-wrapper">
            <ClientCardInfo selectedItem={selectedItem} />
          </div>
        ) : null }
      </div>
      <div className="client-form-control">
        <CustomButton text="Сохранить" onClick={updateClient} />
        <CustomButton isCancel text="Закрыть" onClick={onHide} />
      </div>
      <ImagePreviewModal open={Boolean(previewImage)} imageUrl={previewImage} title={previewTitle} onClose={closePreview} />
    </div>
  );
}
